/**
 * «Кому дозванивались» — блок карточки звонка: лог Voximplant запрашивается при открытии
 * (`GET /api/calls/{id}/route`). Сверху операторы с итогом (как вопрос о пропущенном в «Новости КЦ»),
 * ниже круги очереди по ногам: когда пригласили, когда зазвонил телефон, чем кончилось.
 * Спека `docs/specs/2026-10-20-call-route-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";

import { type CallRoute as Route, loadCallRoute, type RouteKind } from "@/lib/callRoute";

const TONE: Record<RouteKind, string> = {
  answered: "text-ok",
  ask: "text-err",
  unreached: "text-warn",
  busy: "text-fg-muted",
  short: "text-fg-muted",
  off: "text-fg-faint",
  other: "text-fg-muted",
};

const PHONE: Record<string, string> = {
  ring: "звонил",
  offline: "не в сети",
  busy: "занят",
  decline: "отклонил",
  limit: "отбит лимитом",
};

const t = (iso: string | null | undefined) => (iso ? iso.slice(11, 21) : "—");

function Rounds({ r }: { r: Route }) {
  return (
    <div className="flex flex-col gap-3" data-annot="круги очереди">
      {(r.rounds ?? []).map((k, i) => (
        <div key={k.at + i} className="flex flex-col gap-1">
          <div className="text-[12.5px] font-medium">
            <span className="tabular-nums text-fg-muted">{t(k.at)}</span> · круг {i + 1}: {k.queue || "—"}
          </div>
          {k.legs.length === 0 ? (
            <div className="text-[12px] text-fg-faint">никого не звали — клиент ждал в очереди</div>
          ) : (
            <table className="w-full max-w-[64rem] text-[12px]">
              <thead className="text-left text-[11px] text-fg-faint">
                <tr>
                  <th className="py-0.5 pr-3 font-normal">Сотрудник</th>
                  <th className="py-0.5 pr-3 font-normal">Куда</th>
                  <th className="py-0.5 pr-3 font-normal">Пригласили</th>
                  <th className="py-0.5 pr-3 font-normal">Зазвонил</th>
                  <th className="py-0.5 pr-3 font-normal">Конец</th>
                  <th className="py-0.5 pr-3 text-right font-normal">Звонил, с</th>
                  <th className="py-0.5 font-normal">Чем кончилось</th>
                </tr>
              </thead>
              <tbody>
                {k.legs.map((l, j) => (
                  <tr key={j} className={`border-t border-border ${l.ringing || l.connected ? "" : "text-fg-faint"}`}>
                    <td className="py-0.5 pr-3">{l.name}</td>
                    <td className="py-0.5 pr-3">{l.device === "phone" ? "телефон" : "Bitrix в браузере"}</td>
                    <td className="py-0.5 pr-3 tabular-nums">{t(l.invited)}</td>
                    <td className="py-0.5 pr-3 tabular-nums">{t(l.ringing)}</td>
                    <td className="py-0.5 pr-3 tabular-nums">{t(l.connected ?? l.ended)}</td>
                    <td className="py-0.5 pr-3 text-right tabular-nums">{l.rang ? l.rang.toFixed(1) : "—"}</td>
                    <td className={`py-0.5 ${l.connected ? "text-ok" : ""}`}>{l.end || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {k.tabs.length > 0 && (
            <div className="text-[11.5px] text-fg-muted">
              Bitrix в браузере:{" "}
              {k.tabs.map((x) => `${x.name} — ${x.command === "wait" ? "показал звонок" : x.command === "busy" ? "занят" : x.command}`).join(" · ")}
            </div>
          )}
        </div>
      ))}
      <p className="max-w-[64rem] text-[11.5px] text-fg-faint">
        «Сняла очередь (603)» — вызов снял сам сценарий Voximplant: у всех ног круга в одну миллисекунду, когда круг очереди (10 с) кончился или
        клиент положил трубку. Это не отказ оператора: отказ с телефона пришёл бы ответом телефона без команды сценария.
      </p>
    </div>
  );
}

function Body({ r }: { r: Route }) {
  const kinds = r.kinds ?? {};
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]">
        <span>
          Клиент ждал <b className="tabular-nums">{r.waited} с</b>
        </span>
        <span className="text-fg-muted">{r.line}</span>
        {r.queues && r.queues.length > 0 && <span className="text-fg-muted">{r.queues.join(" → ")}</span>}
        {r.kind !== undefined && <span className={TONE[r.kind as RouteKind] ?? ""}>итог: {kinds[r.kind] ?? r.kind}</span>}
        {r.log_url && (
          <a href={r.log_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-accent hover:underline">
            лог Voximplant <ExternalLink size={11} aria-hidden />
          </a>
        )}
      </div>

      <table className="w-full max-w-[64rem] text-[12.5px]" data-annot="операторы звонка">
        <thead className="text-left text-[11px] text-fg-faint">
          <tr>
            <th className="py-1 pr-3 font-normal">Сотрудник</th>
            <th className="py-1 pr-3 font-normal">Итог</th>
            <th className="py-1 pr-3 text-right font-normal" title="Сколько очередь отдавала ему звонок">
              Звонок шёл, с
            </th>
            <th className="py-1 pr-3 text-right font-normal" title="Из них — пока был «на линии» в CRM и не в своём звонке">
              На линии и свободен, с
            </th>
            <th className="py-1 pr-3 text-right font-normal" title="Сколько из них звонил его SIP-телефон (MicroSIP)">
              Звонил телефон, с
            </th>
            <th className="py-1 pr-3 font-normal">Телефон</th>
            <th className="py-1 font-normal">Bitrix в браузере</th>
          </tr>
        </thead>
        <tbody>
          {(r.operators ?? []).map((o) => (
            <tr key={o.user_id} className="border-t border-border">
              <td className="py-1 pr-3">{o.name}</td>
              <td className={`py-1 pr-3 ${TONE[o.kind]}`}>{r.operator_kinds?.[o.kind] ?? o.kind}</td>
              <td className="py-1 pr-3 text-right tabular-nums">{o.offered.toFixed(1)}</td>
              <td className="py-1 pr-3 text-right tabular-nums">{o.free.toFixed(1)}</td>
              <td className="py-1 pr-3 text-right tabular-nums">{o.rang ? o.rang.toFixed(1) : "—"}</td>
              <td className="py-1 pr-3">{PHONE[o.phone] ?? "—"}</td>
              <td className="py-1">
                {o.tab ? "показал звонок" : "—"}
                {o.browser_text && (
                  <div className="text-[11.5px] text-fg-muted" title="По событиям расширения «Прокат» во вкладке Bitrix">
                    {o.browser_text}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {r.later && r.later.length > 0 && (
        <div className="text-[12px] text-fg-muted">
          Потом с клиентом говорили:{" "}
          {r.later.map((x) => `${x.at.slice(11, 16)} ${x.name || "—"} (${x.incoming ? "клиент позвонил" : "перезвонили"})`).join(" · ")}
        </div>
      )}

      <Rounds r={r} />
    </>
  );
}

export function CallRoute({ id }: { id: number }) {
  const q = useQuery({ queryKey: ["call-route", id], queryFn: () => loadCallRoute(id), staleTime: 5 * 60_000 });
  return (
    <section className="flex flex-col gap-2" data-annot="кому дозванивались">
      <h2 className="text-[13px] font-medium">Кому дозванивались</h2>
      {q.isPending ? (
        <span className="text-[12.5px] text-fg-muted">Запрашиваю лог Voximplant…</span>
      ) : q.isError ? (
        <span className="text-[12.5px] text-err">Не удалось: {String(q.error.message)}</span>
      ) : !q.data.incoming ? (
        <span className="text-[12.5px] text-fg-faint">исходящий — очереди нет</span>
      ) : !q.data.log ? (
        <span className="text-[12.5px] text-fg-faint">{q.data.error ?? "у звонка нет лога Voximplant"}</span>
      ) : (
        <Body r={q.data} />
      )}
    </section>
  );
}
